// client/src/components/mischief/Sticker.js
//
// Tiny uppercase chip that looks slapped on. Bigger tilt than
// Scrawl or Highlight because a sticker is a physical object —
// nobody applies a sticker perfectly straight.
//
// Usage:
//   <Sticker>HOME</Sticker>
//   <Sticker tone="win">WIN 4–2</Sticker>
//   <Sticker tone="accent" rotate={3}>CUP</Sticker>
//
// Often positioned absolutely over other content via className:
//   <div className="relative">
//     <Sticker className="absolute -top-2 -right-2">WIN</Sticker>
//     ...card content...
//   </div>

import { useMemo } from 'react';

// Bigger tilt range than the other primitives. A sticker looks
// wrong at 1° — that reads as "sloppy alignment." 5° reads as
// "someone stuck this on with intent." Skip 0 and ±1 entirely.
const TILTS = [-5, -4, -3, -2, 2, 3, 4, 5];
const pickTilt = () => TILTS[Math.floor(Math.random() * TILTS.length)];

// Tone → text color + background color + border color.
// Each tone is a self-contained visual identity. Border comes from
// a slightly darker or matched shade to give the chip physical edge.
const TONE_CLASS = {
  default: 'bg-paper      text-ink       border-rule',
  win:     'bg-green/15   text-green     border-green/40',
  loss:    'bg-red/15     text-red       border-red/40',
  tie:     'bg-inkSub/15  text-inkSub    border-inkSub/30',
  accent:  'bg-accentSoft text-accentDeep border-accent/40',
  marker:  'bg-marker/15  text-marker    border-marker/40',
};

export function Sticker({
  children,
  tone = 'default',
  rotate,
  className = '',
}) {
  // Stable tilt across re-renders. Same trick as Scrawl and Highlight.
  const tilt = useMemo(
    () => (typeof rotate === 'number' ? rotate : pickTilt()),
    [rotate]
  );

  const toneClasses = TONE_CLASS[tone] ?? TONE_CLASS.default;

  return (
    <span
      className={[
        // Layout: inline-block so rotation works and the chip stays
        // on its own visual line without being a block.
        'inline-block',
        // Type: 9px Manrope 800 uppercase with real letter-spacing.
        // font-sans is Manrope; font-extrabold is 800.
        'font-sans font-extrabold uppercase',
        'text-sticker leading-none',
        // Shape: pill-rounded, 1px border, small padding.
        'rounded-full border px-1.5 py-0.5',
        // Optical: nudge slightly so descenders don't overshoot the pill.
        'align-middle',
        // Tone + passthrough.
        toneClasses,
        className,
      ].filter(Boolean).join(' ')}
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      {children}
    </span>
  );
}